(function () {
  // A measured bag rectangle establishes inches per image percentage.
  // Changing only the allowed patch area must never change physical patch scale.
  function physicalPatchSize(a, d) {
    const b=a.bagMeasurement;
    const calibrated=b&&b.confirmed&&['width','height','widthInches','heightInches'].every(k=>Number.isFinite(Number(b[k]))&&Number(b[k])>0);
    const widthInches=calibrated?Number(b.widthInches)*(Number(a.width)||100)/Number(b.width):Number(a.physicalWidth);
    const heightInches=calibrated?Number(b.heightInches)*(Number(a.height)||100)/Number(b.height):Number(a.physicalHeight);
    const measured=Number.isFinite(Number(d.width))&&Number(d.width)>0&&Number.isFinite(Number(d.height))&&Number(d.height)>0;
    if(calibrated&&!measured)return {width:Infinity,height:Infinity,missingMeasurements:true};
    if(!(widthInches>0&&heightInches>0&&measured))return null;
    return {width:Number(d.width)/widthInches,height:Number(d.height)/heightInches};
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (Number.isNaN(number) || number == null) {
      return '0';
    }

    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    const moneyFormat = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';

    let value = '';
    switch (placeholder) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  // A whole patch rectangle must fit the merchant's outline, including concave edges.
  function fitsOutline(box, polygon) {
    if (!polygon?.length) return true;
    const epsilon=1e-7;
    function contains(x,y) {
      let inside=false;
      for(let i=0,j=polygon.length-1;i<polygon.length;j=i++) {
        const a=polygon[j],b=polygon[i];
        const cross=(x-a[0])*(b[1]-a[1])-(y-a[1])*(b[0]-a[0]);
        if(Math.abs(cross)<epsilon&&x>=Math.min(a[0],b[0])-epsilon&&x<=Math.max(a[0],b[0])+epsilon&&y>=Math.min(a[1],b[1])-epsilon&&y<=Math.max(a[1],b[1])+epsilon)return true;
        if((a[1]>y)!==(b[1]>y)&&x<(b[0]-a[0])*(y-a[1])/(b[1]-a[1])+a[0])inside=!inside;
      }
      return inside;
    }
    const {x,y,width:w,height:h}=box;
    if(![[x,y],[x+w,y],[x,y+h],[x+w,y+h]].every(([xx,yy])=>contains(xx,yy)))return false;
    // Reject an outline edge crossing the rectangle's interior, even if corners fit.
    for(let i=0;i<polygon.length;i++) {
      const a=polygon[i],b=polygon[(i+1)%polygon.length];let lo=0,hi=1;
      for(const [origin,delta,min,max] of [[a[0],b[0]-a[0],x+epsilon,x+w-epsilon],[a[1],b[1]-a[1],y+epsilon,y+h-epsilon]]) {
        if(Math.abs(delta)<epsilon){if(origin<min||origin>max){hi=-1;break;}}
        else {let u=(min-origin)/delta,v=(max-origin)/delta;if(u>v)[u,v]=[v,u];lo=Math.max(lo,u);hi=Math.min(hi,v);}
      }
      if(lo<=hi)return false;
    }
    return true;
  }
  function nearestOutline(box, polygon) {
    if(fitsOutline(box,polygon))return {x:box.x,y:box.y};
    let best=null,distance=Infinity;
    for(let yi=0;yi<=64;yi++)for(let xi=0;xi<=64;xi++) {
      const x=(1-box.width)*xi/64,y=(1-box.height)*yi/64,d=(x-box.x)**2+(y-box.y)**2;
      if(d<distance&&fitsOutline({...box,x,y},polygon)){distance=d;best={x,y};}
    }
    return best;
  }

  // Slide along an edge instead of losing the whole diagonal pointer movement.
  // Drag frames only search between known safe positions; no global grid scan.
  function slideOutline(box, polygon, safe) {
    if(fitsOutline(box,polygon))return {x:box.x,y:box.y};
    if(!safe||!fitsOutline({...box,...safe},polygon))return nearestOutline(box,polygon);
    function toward(from,target) {
      if(fitsOutline({...box,...target},polygon))return target;
      let lo=0,hi=1;
      for(let i=0;i<10;i++){
        const t=(lo+hi)/2;
        if(fitsOutline({...box,x:from.x+(target.x-from.x)*t,y:from.y+(target.y-from.y)*t},polygon))lo=t;else hi=t;
      }
      return {x:from.x+(target.x-from.x)*lo,y:from.y+(target.y-from.y)*lo};
    }
    const target={x:box.x,y:box.y};
    const direct=toward(safe,target);
    const horizontal=toward(safe,{x:box.x,y:safe.y});
    const vertical=toward(safe,{x:safe.x,y:box.y});
    const candidates=[direct,toward(horizontal,{x:horizontal.x,y:box.y}),toward(vertical,{x:box.x,y:vertical.y})];
    return candidates.reduce((best,p)=>(p.x-box.x)**2+(p.y-box.y)**2<(best.x-box.x)**2+(best.y-box.y)**2?p:best);
  }

  const instances = new WeakMap();
  let artworkLoader;
  function loadArtwork(url) {
    if (window.BeccoPatchArtwork) return Promise.resolve(window.BeccoPatchArtwork);
    if (!artworkLoader) artworkLoader = new Promise((resolve,reject) => {
      const script = document.createElement('script'); script.src=url; script.async=true;
      script.onload=()=>resolve(window.BeccoPatchArtwork);
      script.onerror=()=>{artworkLoader=null;script.remove();reject(Error('Patch images could not load. Please try again.'))};
      document.head.append(script);
    });
    return artworkLoader;
  }
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  function read(root, selector, fallback) {
    try { return JSON.parse(root.querySelector(selector)?.textContent) ?? fallback; }
    catch { return fallback; }
  }
  function initStudio(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: abort.signal });
    const query = (selector) => root.querySelector(selector);
    const layer = query('[data-bcc-nodes]');
    const canvas = query('[data-bcc-canvas]');
    const image = query('.bcc-studio__canvas-image');
    const tray = query('[data-bcc-tray]');
    const right = query('.bcc-studio__right');
    if (right && !query('.bcc-studio__catalog-panel')) {
      const panel = document.createElement('div'); panel.className = 'bcc-studio__catalog-panel';
      while (right.firstElementChild && !right.firstElementChild.matches('[data-bcc-selection]')) panel.append(right.firstElementChild);
      right.prepend(panel);
    }
    const selectionList = query('[data-bcc-selection]');
    let selectionSummary = query('[data-bcc-selection-summary]');
    if (selectionList && !selectionSummary) {
      selectionSummary = document.createElement('details');
      selectionSummary.className = 'bcc-studio__selection-summary';
      selectionSummary.dataset.bccSelectionSummary = '';
      const heading = document.createElement('summary');
      heading.textContent = 'Summary';
      selectionSummary.append(heading);
      selectionList.before(selectionSummary);
      selectionSummary.append(selectionList);
      let animation = null, expanded = false;
      on(heading, 'click', event => {
        event.preventDefault();
        expanded = !expanded;
        const start = selectionSummary.getBoundingClientRect().height;
        animation?.cancel();
        selectionSummary.style.height = '';
        selectionSummary.open = expanded;
        const end = selectionSummary.getBoundingClientRect().height;
        selectionSummary.dataset.expanded = String(expanded);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Keep the rows rendered while collapsing; a new click starts from the
        // current animated height instead of queuing another transition.
        selectionSummary.open = true;
        selectionSummary.style.overflow = 'hidden';
        animation = selectionSummary.animate([{height:`${start}px`},{height:`${end}px`}], {duration:180,easing:'ease-out'});
        animation.onfinish = () => {
          selectionSummary.open = expanded;
          selectionSummary.style.overflow = '';
          animation = null;
        };
      });
      on(abort.signal, 'abort', () => animation?.cancel());
    }

    const cta = query('[data-bcc-add-set]');
    if (!layer || !image || !cta) return;
    const variants = read(root, '[data-bcc-base-variants]', []);
    const patches = read(root, '[data-bcc-patches]', []);
    const catalog = new Map(patches.map((p) => [String(p.id), p]));
    const buttons = Array.from(root.querySelectorAll('[data-bcc-patch]'));
    const selections = new Map();
    const deletePiece = document.createElement('button');
    deletePiece.type='button'; deletePiece.className='bcc-studio__delete-piece'; deletePiece.hidden=true;
    deletePiece.setAttribute('aria-label','Delete selected patch'); deletePiece.title='Delete selected patch';
    deletePiece.innerHTML='<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 10v8m4-8v8"/></svg>';
    canvas.append(deletePiece);
    on(deletePiece,'click',()=>{if(selectedNode&&!busy)removeNode(selectedNode)});
    const preparing = new Set();
    const productPreparation = new Map();
    let studioVisible = false, scrollQuietAt = 0;
    on(tray, 'scroll', () => { scrollQuietAt = Date.now() + 180; });
    let selectionEpoch = 0;
    let pagination = read(root, '[data-bcc-pagination]', {});
    let loadingCatalog = false;
    let variant = variants.find((v) => String(v.id) === root.dataset.baseVariantId) || variants[0];
    let activeTab = 'patches';
    let selectedNode = null;
    let busy = false;
    let frame = 0;
    let dragCleanup = null;
    let suppressClick = false;
    let pendingPiece = null, stageRequest = 0, stageTab = null;
    const stage = document.createElement('section');
    stage.className = 'bcc-studio__piece-stage'; stage.hidden = true;
    tray.before(stage);
    function armPiece(id, part) {
      pendingPiece = { id, pieceId: String(part.id) };
      stage.querySelectorAll('[data-piece-id]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.pieceId === pendingPiece.pieceId)));
      say('Now click or tap the bag where you want this patch. You can also drag it there.');
      if (window.matchMedia('(max-width: 989px)').matches) openStudio();
    }
    async function openSet(id) {
      const p = catalog.get(id); if (!p || !p.available || busy) return;
      if (window.matchMedia('(max-width: 989px)').matches) openStudio();
      const request = ++stageRequest; pendingPiece = null;
      stageTab = activeTab;
      stage.hidden = false; stage.textContent = 'Preparing set pieces…'; choose(null);
      try {
        const parts = root.dataset.artworkUrl ? await prepareProduct(p) : pieces(p);
        if (request !== stageRequest || abort.signal.aborted) return;
        stage.replaceChildren();
        const title = document.createElement('strong'); title.textContent = p.title;
        const hint = document.createElement('p'); hint.textContent = `Click a piece to add it, or drag it onto your bag. Full set: ${money(p.price)}.`;
        const list = document.createElement('div'); list.className = 'bcc-studio__staged-pieces';
        parts.forEach((part, index) => {
          const b = document.createElement('button'); b.type = 'button'; b.className = 'bcc-studio__patch';
          b.dataset.id = id; b.dataset.pieceId = String(part.id);
          b.setAttribute('aria-label', `${p.title}, piece ${index + 1}: place on bag`);
          const img = document.createElement('img'); img.src = part.src; img.alt = ''; img.draggable = false; b.append(img);
          on(b, 'pointerdown', e => startDrag(e, b, null));
          on(b, 'dragstart', e => e.preventDefault());
          on(b, 'click', () => { if (!suppressClick) { pendingPiece = null; addPatch(id, null, String(part.id)); } });
          list.append(b);
        });
        const dismiss = document.createElement('button'); dismiss.type='button'; dismiss.className='bcc-studio__stage-close'; dismiss.textContent='×'; dismiss.setAttribute('aria-label','Close set pieces');
        on(dismiss,'click',()=>{stage.hidden=true;stageTab=null;pendingPiece=null;stageRequest++;});
        stage.append(dismiss, title, hint, list);
        say('Choose a piece from the open set and place it on your bag.');
      } catch (error) { stage.textContent = error.message; }
    }
    on(canvas, 'click', event => {
      if (event.target.closest('.bcc-studio__node,.bcc-studio__delete-piece') || suppressClick) return;
      choose(null);
      if (!pendingPiece) return;
      const rect = layer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
      addPatch(pendingPiece.id, {x:(event.clientX-rect.left)/rect.width,y:(event.clientY-rect.top)/rect.height}, pendingPiece.pieceId);
      pendingPiece = null;
      stage.querySelectorAll('[aria-pressed]').forEach(b => b.setAttribute('aria-pressed','false'));
    });
    let geometry = { width: 1, height: 1 };
    const say = (text) => { query('[data-bcc-status]').textContent = text; };
    const money = (price) => formatMoney(price, root.dataset.moneyFormat);
    const isSet = (p) => p.isSet ?? ((p.tags || []).some((tag) => tag.toLowerCase() === 'patch-set') || /\b(?:set|pack|tiles|gems)\b/i.test(p.title) || (p.preparedPieces?.length || 0) > 1);
    const pieces = (p) => p.preparedPieces || (isSet(p) ? (p.images || []).slice(1) : (p.images || []).slice(0, 1));
    const area = () => {
      const a = variant?.area || read(root, '[data-bcc-area]', null) || {};
      const x = clamp(Number(a.x) || 0, 0, 99);
      const y = clamp(Number(a.y) || 0, 0, 99);
      return { x, y, width: clamp(Number(a.width) || 100, 1, 100 - x), height: clamp(Number(a.height) || 100, 1, 100 - y), outline: Array.isArray(a.outline) ? a.outline.map(p=>[(p[0]-x)/(Number(a.width)||100),(p[1]-y)/(Number(a.height)||100)]) : null, bagMeasurement: a.bagMeasurement, physicalWidth: Number(a.physicalWidth), physicalHeight: Number(a.physicalHeight), previewPatchWidth: Number(a.previewPatchWidth) };
    };
    function dimensions(p, part) {
      const list = Array.isArray(p.dimensions) ? p.dimensions : [];
      const compatible=d=>!part.refined || (d.crop&&part.crop&&['x','y','width','height'].every(key=>{const axis=key==='x'||key==='width'?'sourceWidth':'sourceHeight';return Math.abs(d.crop[key]/d.crop[axis]-part.crop[key]/part.crop[axis])<.01}));
      return list.find((d) => String(d.imageId) === String(part.id)&&compatible(d)) ||
        ((p.preparedPieces?.length || 0) <= 1 ? list.find((d) => String(d.imageId) === String(part.sourceImageId)) : null) ||
        (p.images.indexOf(part) >= 0 ? list.find((d) => Number(d.image) === p.images.indexOf(part) + 1) : null) || {};
    }
    function size(p, part, a = area()) {
      const d = dimensions(p, part);
      const actual = physicalPatchSize(a, d);
      if (actual) return actual;
      const largest = Math.max(...(p.preparedPieces || []).map(piece => Math.max(piece.crop?.width || 0,piece.crop?.height || 0)), Math.max(part.crop?.width || 1,part.crop?.height || 1));
      // Keep uncalibrated artwork legible, including small pieces in a mixed set.
      // Physical dashboard measurements still take precedence when configured.
      const relativeSize = part.crop ? clamp(Math.max(part.crop.width, part.crop.height) / largest, 0.8, 1) : 1;
      const aspect = part.aspectRatio || 1;
      const previewWidth = a.previewPatchWidth > 0 ? clamp(a.previewPatchWidth / 100, 0.01, 1) : (a.outline?.length ? 0.065 : 0.12);
      const width = previewWidth * relativeSize * Math.min(1, aspect);
      const height = width * geometry.width / geometry.height / aspect;
      const fit = Math.min(1, 1 / width, 1 / height);
      return { width: width * fit, height: height * fit };
    }
    function placeDelete() {
      deletePiece.hidden=!selectedNode;
      if(!selectedNode)return;
      const l=layer.getBoundingClientRect(), c=canvas.getBoundingClientRect();
      const x=l.left-c.left+(selectedNode.x+selectedNode.width)*l.width;
      const y=l.top-c.top+selectedNode.y*l.height;
      deletePiece.style.left=`${clamp(x+5,0,c.width-34)}px`;
      deletePiece.style.top=`${clamp(y-12,0,c.height-34)}px`;
    }
    let boundaryGuide, boundaryActive=false, boundaryHit=false, boundaryPoints='';
    function showBoundary(visible) {
      if(!boundaryGuide){
        boundaryGuide=document.createElementNS('http://www.w3.org/2000/svg','svg');
        boundaryGuide.setAttribute('viewBox','0 0 100 100');boundaryGuide.setAttribute('preserveAspectRatio','none');
        boundaryGuide.classList.add('bcc-studio__boundary');boundaryGuide.setAttribute('aria-hidden','true');
        boundaryGuide.innerHTML='<polygon fill="none" stroke="#d92d20" stroke-width="3" vector-effect="non-scaling-stroke" />';
        layer.append(boundaryGuide);
      }
      const points='0,0 100,0 100,100 0,100';
      if(points!==boundaryPoints){boundaryGuide.querySelector('polygon').setAttribute('points',points);boundaryPoints=points;}
      boundaryGuide.style.visibility=visible?'visible':'hidden';
    }
    function position(node) {
      const attempted={x:node.x,y:node.y,width:node.width,height:node.height};
      const a=area();
      if(attempted.x<0||attempted.y<0||attempted.x+attempted.width>1||attempted.y+attempted.height>1||!fitsOutline(attempted,a.outline))boundaryHit=true;
      showBoundary(boundaryActive&&boundaryHit);
      node.x = clamp(node.x, 0, Math.max(0, 1 - node.width));
      node.y = clamp(node.y, 0, Math.max(0, 1 - node.height));
      const outline=a.outline;
      const constrained=slideOutline(node,outline,node.safePosition);
      if (constrained) { Object.assign(node, constrained); node.safePosition=constrained; }
      else if(node.safePosition)Object.assign(node,node.safePosition);
      Object.assign(node.el.style, { left: `${node.x * 100}%`, top: `${node.y * 100}%`, width: `${node.width * 100}%`, height: `${node.height * 100}%` });
      if(node===selectedNode)placeDelete();
    }
    function layout() {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const ratio = image.naturalWidth / image.naturalHeight || 1;
      const bounds = {x:0,y:0,width:1,height:1};
      const width = Math.min(rect.width*.92/bounds.width, rect.height*.92*ratio/bounds.height);
      const height = width / ratio;
      const left=(rect.width-width*bounds.width)/2-width*bounds.x;
      const top=(rect.height-height*bounds.height)/2-height*bounds.y;
      Object.assign(image.style, {width:`${width}px`,height:`${height}px`,left:`${left}px`,top:`${top}px`,maxWidth:'none'});
      const a = area();
      geometry = { width: width * a.width / 100, height: height * a.height / 100 };
      Object.assign(layer.style, { left: `${left + width * a.x / 100}px`, top: `${top + height * a.y / 100}px`, width: `${geometry.width}px`, height: `${geometry.height}px` });
      selections.forEach((selection) => selection.nodes.forEach((node) => {
        const next = size(selection.patch, node.part);
        node.width = Math.min(1, next.width);
        node.height = Math.min(1, next.height);
        position(node);
      }));
    }
    function choose(node) {
      boundaryActive=!!node;boundaryHit=false;showBoundary(false);
      selectedNode?.el.classList.remove('is-selected');
      selectedNode = node;
      node?.el.classList.add('is-selected');
      query('[data-bcc-remove]').disabled = !node;
      query('[data-bcc-remove]').hidden = !node;
      placeDelete();
    }
    function quantity(selection) {
      const counts = new Map();
      selection.nodes.forEach(n => counts.set(String(n.part.id), (counts.get(String(n.part.id)) || 0) + 1));
      // A second copy of any set piece requires another complete set.
      return Math.max(0, ...counts.values());
    }
    function nextPlacement(d) {
      const occupied = [...selections.values()].flatMap(s => s.nodes);
      const gap = 0.02;
      // Clicks start on the upper front, not in the rectangle's empty top corners.
      // This is only the automatic starting region; manual placement is unchanged.
      const startX = Math.min(0.25, Math.max(0,(1-d.width)/2));
      const startY = Math.min(0.15, Math.max(0,1-d.height));
      const rowEnd = Math.max(0.75,startX+d.width);
      const xs = [...new Set([startX, ...occupied.map(n=>n.x+n.width+gap)])].filter(x=>x>=startX&&x+d.width<=rowEnd+1e-7).sort((a,b)=>a-b);
      const ys = [...new Set([startY, ...occupied.flatMap(n=>[n.y,n.y+n.height+gap])])].filter(y=>y>=startY).sort((a,b)=>a-b);
      const outline = area().outline;
      let best = null, leastOverlap = Infinity;
      function consider(x,y) {
        const candidate = {x,y,...d};
        if(x<0 || y<0 || x+d.width>1 || y+d.height>1 || !fitsOutline(candidate,outline)) return false;
        let overlap=0;
        for(const n of occupied) overlap += Math.max(0,Math.min(x+d.width+gap,n.x+n.width)-Math.max(x-gap,n.x))*Math.max(0,Math.min(y+d.height+gap,n.y+n.height)-Math.max(y-gap,n.y));
        if(overlap<leastOverlap){best={x,y};leastOverlap=overlap;}
        return overlap===0;
      }
      for(const y of ys) for(const x of xs) if(consider(x,y)) return best;
      // Keep additional copies usable when the finite bag area is already filled.
      for(let row=0;row<=20;row++) for(let col=0;col<=20;col++) if(consider(col*(1-d.width)/20,row*(1-d.height)/20)) return best;
      return best || nearestOutline({...d,x:0.34,y:0.34},outline) || {x:0,y:0};
    }
    function update() {
      let total = Number(variant?.price || 0), count = 0;
      selections.forEach((selection) => { total += selection.patch.price * quantity(selection);
        const label = selection.row.querySelector("span");
        if (label) label.textContent = `${selection.patch.title} · ${money(selection.patch.price * quantity(selection))} · ${quantity(selection)} ${isSet(selection.patch) || pieces(selection.patch).length > 1 ? "full set(s)" : "patch(es)"}`; count += selection.nodes.length; });
      query('[data-bcc-summary]').textContent = `${root.dataset.baseTitle} · ${variant?.color || ''} · ${count} PATCH${count === 1 ? '' : 'ES'} · ${money(total)}`;
      if (selectionSummary) {
        selectionSummary.hidden = selections.size === 0;
        selectionSummary.querySelector('summary').textContent = `Summary · ${count} patch${count === 1 ? '' : 'es'}`;
      }
      cta.disabled = busy || preparing.size > 0 || !variant?.available;
      buttons.forEach((button) => {
        const selection = selections.get(button.dataset.id);
        const selected = !!selection && (!button.dataset.pieceId || selection.nodes.some(n=>String(n.part.id)===button.dataset.pieceId));
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }
    function removeSelection(id) {
      const selection = selections.get(id);
      if (!selection) return;
      if (selection.nodes.includes(selectedNode)) choose(null);
      selection.nodes.forEach((node) => node.el.remove());
      selection.row.remove();
      selections.delete(id);
      update();
    }
    function removeNode(node) {
      const selection = selections.get(node.productId);
      if (!selection) return;
      selection.nodes = selection.nodes.filter((n) => n !== node);
      node.el.remove(); choose(null);
      if (!selection.nodes.length) removeSelection(node.productId);
      else { say('Patch removed. Quantity updated.'); update(); }
    }
    function addPatch(id, point, pieceId) {
      const patch = catalog.get(id);
      if (!patch || !patch.available || busy) return;

      if (root.dataset.artworkUrl && !patch.preparedPieces) {
        preparing.add(id); const epoch=selectionEpoch; update(); say('Preparing your patches…');
        prepareProduct(patch).then(parts=>{
          patch.preparedPieces=parts; preparing.delete(id); update();
          if (!abort.signal.aborted && epoch===selectionEpoch) addPatch(id,point,pieceId);
        }).catch(error=>{preparing.delete(id);update();say(error.message)});
        return;
      }
      const allParts = pieces(patch);
      if (!pieceId && (isSet(patch) || allParts.length > 1)) { openSet(id); return; }
      const existing = selections.get(id);
      const parts = allParts.filter(part=>(!pieceId || String(part.id)===pieceId));
      if (!parts.length) { say('Individual images for this set are not available yet.'); return; }
      if (parts.some(part=>size(patch,part).missingMeasurements)) {
        say('This patch needs width and height measurements before it can be placed on this true-size bag.'); return;
      }
      if (parts.some((part) => { const d = size(patch, part); return d.width > 1 || d.height > 1 || !nearestOutline({...d,x:.5,y:.5},area().outline); })) {
        say('This patch does not fit the selected bag area. Choose a smaller patch.'); return;
      }
      const selection = existing || { patch, nodes: [], row: document.createElement('div'), monogram: '' };
      if (!existing) {
      selection.row.className = 'bcc-studio__selection-row';
      const title = document.createElement('span');
      title.textContent = `${patch.title} · ${money(patch.price)}${isSet(patch) || allParts.length > 1 ? ` · Full set (${allParts.length} pieces)` : ''}`;
      selection.row.append(title);
      if (patch.offerMonogram) {
        const label = document.createElement('label'); label.textContent = 'Monogram (optional)';
        const input = document.createElement('input'); input.type = 'text'; input.maxLength = 20;
        on(input, 'input', () => { selection.monogram = input.value.trim(); });
        label.append(input); selection.row.append(label);
      }
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${patch.title}`);
      on(remove, 'click', () => removeSelection(id)); selection.row.append(remove);
      query('[data-bcc-selection]').append(selection.row);
      selections.set(id, selection);
      }
      parts.forEach((part, index) => {
        const el = document.createElement('button'); el.type = 'button'; el.className = 'bcc-studio__node';
        const img = document.createElement('img'); img.src = part.src; img.alt = ''; img.draggable = false; el.append(img);
        el.setAttribute('aria-label', `${part.alt || patch.title + ' patch ' + (index + 1)}. Arrow keys move; Delete removes.`);
        const d = size(patch, part);
        const node = { el, part, productId: id, ...d, ...nextPlacement(d) };
        if (point) { node.x = point.x + (index % 3) * 0.12 - d.width / 2; node.y = point.y + Math.floor(index / 3) * 0.15 - d.height / 2; }
        selection.nodes.push(node); layer.append(el); position(node);
        on(el, 'click', () => { if (!suppressClick) choose(node); });
        on(el, 'pointerdown', (event) => startDrag(event, el, node));
        on(el, 'keydown', (event) => {
          if (busy) return;
          const delta = event.shiftKey ? 0.05 : 0.01;
          const moves = { ArrowLeft: [-delta, 0], ArrowRight: [delta, 0], ArrowUp: [0, -delta], ArrowDown: [0, delta] };
          if (moves[event.key]) { event.preventDefault(); choose(node); node.x += moves[event.key][0]; node.y += moves[event.key][1]; position(node); }
          if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); removeNode(node); }
        });
      });
      choose(null); say(isSet(patch) || allParts.length > 1 ? 'Patch added. The full set is included in your order.' : 'Patch added.'); update();
    }
    // Only the dragged element is updated, at most once per animation frame.
    function startDrag(event, source, node) {
      if (busy || event.button !== 0 || !event.isPrimary) return;
      if (!node && event.pointerType === 'touch' && !event.heldTouch) return;
      if (!node && source.disabled) return;
      if (event.pointerType === 'touch') event.preventDefault();
      if(!node && root.dataset.artworkUrl) {
        const p=catalog.get(source.dataset.id);
        if(p&&!p.preparedPieces){prepareProduct(p).catch(error=>say(error.message));if(event.pointerType!=='touch'){say('Preparing this patch…');return;}}
      }
      dragCleanup?.();
      let rect = layer.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const startX = event.clientX, startY = event.clientY;
      const initial = node ? { x: node.x, y: node.y } : null;
      let moved = false, ghost = null, latest = event, lastX=startX, lastY=startY;
      try { source.setPointerCapture(event.pointerId); } catch {}
      if (node) { event.preventDefault(); choose(null); }
      boundaryActive=true;boundaryHit=false;
      function paint() {
        frame = 0;
        if (node) { node.x += (latest.clientX - lastX) / rect.width; node.y += (latest.clientY - lastY) / rect.height; lastX=latest.clientX;lastY=latest.clientY;position(node); }
        else if (ghost) {
          ghost.style.transform = `translate(${latest.clientX - 35}px, ${latest.clientY - 35}px)`;
          const p=catalog.get(source.dataset.id),part=pieces(p).find(p=>String(p.id)===source.dataset.pieceId)||pieces(p)[0];
          if(part){const d=size(p,part),x=(latest.clientX-rect.left)/rect.width-d.width/2,y=(latest.clientY-rect.top)/rect.height-d.height/2;
            if(x<0||y<0||x+d.width>1||y+d.height>1||!fitsOutline({x,y,...d},area().outline))boundaryHit=true;showBoundary(boundaryActive&&boundaryHit);}
        }
      }
      function move(e) {
        if (e.pointerId !== event.pointerId) return;
        latest = e;
        if (!moved && Math.hypot(e.clientX - startX, e.clientY - startY) < 6) return;
        moved = true;
        if (!node && !ghost) { ghost = source.querySelector('img')?.cloneNode(); if (ghost) { ghost.className = 'bcc-studio__drag-ghost'; ghost.removeAttribute('srcset'); const p=catalog.get(source.dataset.id); const prepared=pieces(p).find(p=>String(p.id)===source.dataset.pieceId)||pieces(p)[0]; if(prepared)ghost.src=prepared.src; ghost.alt = ''; (root.closest('dialog') || document.body).append(ghost); } }
        if (!frame) frame = requestAnimationFrame(paint);
      }
      function clean() {
        cancelAnimationFrame(frame); frame = 0; ghost?.remove(); showBoundary(boundaryActive&&boundaryHit);
        document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', end); document.removeEventListener('pointercancel', end);
        document.removeEventListener('touchmove', touchMove); document.removeEventListener('touchend', touchEnd); document.removeEventListener('touchcancel', touchEnd);
        if (source.hasPointerCapture(event.pointerId)) source.releasePointerCapture(event.pointerId);
        dragCleanup = null;
      }
      function end(e) {
        if (e.pointerId !== event.pointerId) return;
        latest = e;
        rect = layer.getBoundingClientRect();
        if (e.type === 'pointercancel' && node) { Object.assign(node, initial); position(node); }
        else if (moved && e.type !== 'pointercancel') {
          paint();
          if (!node && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) addPatch(source.dataset.id, { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height }, source.dataset.pieceId);
        }
        if (moved) pendingPiece = null;
        suppressClick = moved; setTimeout(() => { suppressClick = false; }, 350); clean();

      }
      function touchPoint(e) {
        const t = [...e.changedTouches].find(t => t.identifier === event.pointerId);
        return t && { clientX:t.clientX, clientY:t.clientY, pointerId:t.identifier, type:e.type === 'touchcancel' ? 'pointercancel' : e.type };
      }
      function touchMove(e) { const t=touchPoint(e); if(t) { e.preventDefault(); move(t); } }
      function touchEnd(e) { const t=touchPoint(e); if(t) { if(moved) e.preventDefault(); end(t); } }
      if (event.heldTouch) {
        document.addEventListener('touchmove', touchMove, {passive:false});
        document.addEventListener('touchend', touchEnd, {passive:false});
        document.addEventListener('touchcancel', touchEnd, {passive:false});
      } else {
        document.addEventListener('pointermove', move); document.addEventListener('pointerup', end); document.addEventListener('pointercancel', end);
      }
      dragCleanup = clean;
    }
    function filter() {
      stage.hidden = stageTab !== activeTab;
      const search = query('[data-bcc-search]').value.toLocaleLowerCase().trim();
      const category = query('[data-bcc-filter]').value.toLocaleLowerCase();
      let count = 0;
      buttons.forEach((button) => {
        const p = catalog.get(button.dataset.id);
        if (!p) return;
        const text = `${p.title} ${p.type} ${(p.tags || []).join(' ')}`.toLocaleLowerCase();
        const categoryMatch = category === 'all' || category.split(/[,|/]/).some((term) => {
          const keyword = term.trim();
          if (keyword === 'letters' || keyword === 'letter') return /letter|alphabet/i.test(p.title);
          if (keyword === 'icons' || keyword === 'icon') return !/letter|alphabet/i.test(p.title);
          return text.includes(keyword);
        });
        const inStock = p.available;
        const representation = !button.dataset.pieceId && (isSet(p) ? 'sets' : 'patches') === activeTab;
        const match = representation && text.includes(search) && categoryMatch && inStock;
        button.hidden = !match; if (match) count++;
      });
      query('[data-bcc-count]').textContent = `${count} ${activeTab} available`;
      query('[data-bcc-empty]').hidden = count > 0;
      tray.setAttribute('aria-label', activeTab === 'sets' ? 'Patch sets' : 'Patches');
    }
    function changeVariant(id, notify = false) {
      const next = variants.find((v) => String(v.id) === String(id));
      if (!next || busy) return;
      const candidateArea = next.area || read(root, '[data-bcc-area]', null) || {};
      const tooLarge = [...selections.values()].some(selection => selection.nodes.some(node => {
        const d = size(selection.patch, node.part, candidateArea);
        const outline = candidateArea.outline?.map(p=>[(p[0]-(candidateArea.x||0))/(candidateArea.width||100),(p[1]-(candidateArea.y||0))/(candidateArea.height||100)]);
        return d.width > 1 || d.height > 1 || !nearestOutline({...d,x:.5,y:.5},outline);
      }));
      if (tooLarge) {
        say('A selected patch is too large for this bag area. Remove it before changing color.');
        root.querySelectorAll('[data-bcc-option]').forEach(select => { select.value = variant.options[Number(select.dataset.bccOption)]; });
        return;
      }
      variant = next;
      if (next.image && image.dataset.bccSource !== next.image) { image.dataset.bccSource=next.image; image.removeAttribute('srcset'); image.src = next.image; }
      root.querySelectorAll('[data-bcc-option]').forEach((select) => { select.value = next.options[Number(select.dataset.bccOption)]; });
      root.querySelectorAll('[data-bcc-color-value]').forEach((button) => {
        button.setAttribute('aria-pressed', String(next.options[Number(button.dataset.bccColorIndex)] === button.dataset.bccColorValue));
      });
      if (query('[data-bcc-selected-color]')) query('[data-bcc-selected-color]').textContent = next.color || '';
      layout(); update();
      if (notify) {
        // Filtering the product gallery above changes document height. Keep the
        // inline Studio anchored where the shopper clicked the color swatch.
        const inline=!root.closest('dialog[open]'),top=root.getBoundingClientRect().top;
        document.dispatchEvent(new CustomEvent('bcc:studio-variant-change', { detail: { productId: root.dataset.baseProductId, variantId: next.id } }));
        if(inline&&root.isConnected){const delta=root.getBoundingClientRect().top-top;if(Math.abs(delta)>.5)window.scrollBy({top:delta,left:0,behavior:'instant'});}
      }
    }
    const artworkQueue = [], queuedArtwork = new Set();
    let activeArtwork = 0;
    const artworkObserver = root.dataset.artworkUrl ? new IntersectionObserver(entries=>{
      entries.forEach(entry=>{if(studioVisible && entry.isIntersecting){const p=catalog.get(entry.target.dataset.id);if(p&&!p.trayPrepared&&!queuedArtwork.has(p.id)){queuedArtwork.add(p.id);artworkQueue.unshift(p)}}});
      pumpArtwork();
    },{root:tray,rootMargin:'80px'}) : null;
    function pumpArtwork() {
      while(studioVisible && activeArtwork<2 && artworkQueue.length && !abort.signal.aborted){
        const p=artworkQueue.shift();
        const card=buttons.find(b=>b.dataset.id===String(p.id)&&!b.dataset.pieceId);
        const bounds=tray.getBoundingClientRect(), rect=card?.getBoundingClientRect();
        if (!card || card.hidden || !rect || rect.bottom < bounds.top-80 || rect.top > bounds.bottom+80) { queuedArtwork.delete(p.id); continue; }
        activeArtwork++;
        prepareProduct(p).catch(()=>{queuedArtwork.delete(p.id)}).finally(()=>{activeArtwork--;pumpArtwork()});
      }
    }
    function prepareProduct(p) {
      if (productPreparation.has(p.id)) return productPreparation.get(p.id);
      const promise = prepareProductOnce(p);
      productPreparation.set(p.id, promise);
      promise.catch(() => productPreparation.delete(p.id));
      return promise;
    }
    function preserveTrayPosition(change) {
      const top = tray.getBoundingClientRect().top;
      const anchors = [];
      for (const button of buttons) {
        if (button.hidden) continue;
        const rect = button.getBoundingClientRect();
        if (rect.bottom > top + 1) anchors.push({button,offset:rect.top});
        if (anchors.length === 16) break;
      }
      change();
      const anchor = anchors.find(({button}) => !button.hidden && button.isConnected);
      if (anchor) tray.scrollTop += anchor.button.getBoundingClientRect().top - anchor.offset;
    }
    async function prepareProductOnce(p) {
      if(!p.preparedPieces){
        const api=await loadArtwork(root.dataset.artworkUrl);
        api.configure?.(root.dataset.previewsUrl);
        p.preparedPieces=await api.prepare(p.images[0],p.isSet !== false,false,p.layout?.columns||0);
      }
      while (!abort.signal.aborted && (dragCleanup || Date.now() < scrollQuietAt)) await new Promise(resolve => setTimeout(resolve, 80));
      if(!p.trayPrepared && !abort.signal.aborted){
        p.trayPrepared=true;
        const parent=buttons.find(b=>b.dataset.id===String(p.id)&&!b.dataset.pieceId);
        if(parent){
          artworkObserver?.unobserve(parent);
          preserveTrayPosition(() => {
            if (!isSet(p) && p.preparedPieces[0]) {
              const img = parent.querySelector('img');
              if (img) { img.removeAttribute('srcset'); img.src = p.preparedPieces[0].src; }
            }
            filter();
          });update();
        }
      }
      return p.preparedPieces;
    }
    function bindPatch(button) {
      const patch = catalog.get(button.dataset.id);
      // Set covers open a piece tray; let their swipes use native momentum.
      if (!button.dataset.pieceId && isSet(patch)) button.style.touchAction = 'pan-y';
      button.disabled = !patch?.available || !(root.dataset.artworkUrl ? patch?.images?.length : pieces(patch).length);
      button.title = `${patch?.title || ''}${button.dataset.pieceId ? ' · piece '+button.dataset.pieceNumber : ''} · ${money(patch?.price || 0)}${(patch?.preparedPieces?.length||0)>1?' (full set)':''}`;
      button.setAttribute('aria-label', `Add ${button.title}`);
      on(button, 'click', async () => {
        if (suppressClick) return;
        if (!button.dataset.pieceId && (isSet(patch) || (patch.preparedPieces?.length || 0) > 1)) return openSet(button.dataset.id);
        if (window.matchMedia('(max-width: 989px)').matches) {
          openStudio(); say('Preparing patch…');
          try {
            const parts = root.dataset.artworkUrl ? await prepareProduct(patch) : pieces(patch);
            if (abort.signal.aborted) return;
            if (isSet(patch)) return openSet(button.dataset.id);
            if (parts[0]) addPatch(button.dataset.id, null, String(parts[0].id));
          } catch(error) { say(error.message); }
        } else addPatch(button.dataset.id, null, button.dataset.pieceId);
      });
      on(button, 'pointerenter', () => { if(patch&&!patch.preparedPieces)prepareProduct(patch).catch(()=>{}); });
      on(button, 'focus', () => { if(patch&&!patch.preparedPieces)prepareProduct(patch).catch(()=>{}); });
      on(button, 'pointerdown', (event) => { if (!button.dataset.pieceId && (isSet(patch) || (patch.preparedPieces?.length || 0) > 1)) return; startDrag(event, button, null); });
      on(button, 'dragstart', (event) => event.preventDefault());
      if(!button.dataset.pieceId)artworkObserver?.observe(button);
    }
    // Swipes stay native (including iOS momentum). Only a stationary hold starts a drag.
    let holdTimer = 0, holdStart = null;
    function cancelHold() { clearTimeout(holdTimer); holdTimer=0; holdStart=null; }
    root.addEventListener('touchstart', event => {
      cancelHold();
      const source=event.target.closest('.bcc-studio__patch');
      if(!source || source.disabled || event.touches.length!==1) return;
      const p=catalog.get(source.dataset.id);
      if(!source.dataset.pieceId && isSet(p)) return;
      const t=event.touches[0]; holdStart={x:t.clientX,y:t.clientY};
      holdTimer=setTimeout(()=>{
        holdTimer=0; holdStart=null;
        startDrag({button:0,isPrimary:true,pointerType:'touch',heldTouch:true,pointerId:t.identifier,clientX:t.clientX,clientY:t.clientY,preventDefault(){}},source,null);
      },260);
    }, {passive:true,signal:abort.signal});
    root.addEventListener('touchmove', event => {
      if(holdStart && (event.touches.length!==1 || Math.hypot(event.touches[0].clientX-holdStart.x,event.touches[0].clientY-holdStart.y)>5)) cancelHold();
    }, {passive:true,signal:abort.signal});
    on(root,'touchend',cancelHold); on(root,'touchcancel',cancelHold); on(abort.signal,'abort',cancelHold);
    buttons.forEach(bindPatch);
    on(root, 'contextmenu', event => {
      if (event.target.closest('.bcc-studio__patch,.bcc-studio__staged-pieces button,.bcc-studio__node')) event.preventDefault();
    });
    on(document,'pointerdown',event=>{if(!dragCleanup&&!canvas.contains(event.target))choose(null)});
    choose(null);
    const tabs = Array.from(root.querySelectorAll('[data-bcc-tab]'));
    tabs.forEach((tab, index) => {
      on(tab, 'click', () => { activeTab = tab.dataset.bccTab; if (activeTab === 'patches') observeArtwork(); tabs.forEach((t) => { t.setAttribute('aria-selected', String(t === tab)); t.tabIndex = t === tab ? 0 : -1; }); filter(); });
      on(tab, 'keydown', (e) => { if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) { e.preventDefault(); const next = tabs[e.key === 'Home' ? 0 : e.key === 'End' ? 1 : 1 - index]; next.click(); next.focus(); } });
    });
    on(query('[data-bcc-search]'), 'input', filter); on(query('[data-bcc-filter]'), 'change', filter);
    on(query('[data-bcc-availability]'), 'change', filter);
    let lastSortMode = 'featured';
    function sortCatalog() {
      if (dragCleanup) { setTimeout(() => { if (!abort.signal.aborted) sortCatalog(); }, 400); return; }
      const sorted = buttons.slice();
      const mode = query('[data-bcc-sort]').value;
      if (mode === 'featured' && lastSortMode === 'featured') return;
      lastSortMode = mode;
      if (mode !== 'featured') sorted.sort((a, b) => { const x = catalog.get(a.dataset.id), y = catalog.get(b.dataset.id); return mode === 'title' ? x.title.localeCompare(y.title) : (x.price - y.price) * (mode === 'price-desc' ? -1 : 1); });
      const fragment = document.createDocumentFragment(); sorted.forEach((button) => fragment.append(button)); tray.append(fragment);
    }
    on(query('[data-bcc-sort]'), 'change', sortCatalog);
    on(query('[data-bcc-remove]'), 'click', () => { if (selectedNode && !busy) removeNode(selectedNode); });
    on(query('[data-bcc-clear]'), 'click', () => { if (!busy) { selectionEpoch++; pendingPiece = null; Array.from(selections.keys()).forEach(removeSelection); say(''); } });
    root.querySelectorAll('[data-bcc-option]').forEach((select) => on(select, 'change', () => {
      const options = Array.from(root.querySelectorAll('[data-bcc-option]')).map((s) => s.value);
      const next = variants.find((v) => v.options.every((value, i) => value === options[i]));
      if (next) changeVariant(next.id, true);
      else { say('This option combination is unavailable.'); changeVariant(variant.id); }
    }));
    root.querySelectorAll('[data-bcc-color-value]').forEach((button) => on(button, 'click', () => {
      const select = root.querySelector(`[data-bcc-option="${button.dataset.bccColorIndex}"]`);
      if (!select || busy) return;
      select.value = button.dataset.bccColorValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    on(document, 'bcc:variant-change' , (event) => {
      if (event.detail.productId && String(event.detail.productId) !== root.dataset.baseProductId) return;
      changeVariant(event.detail.variantId);
    });
    on(cta, 'click', async () => {
      if (busy || preparing.size || !variant?.available) return;
      if (Array.from(selections.values()).some((s) => s.nodes.some((n) => { const d = size(s.patch, n.part); return d.width > 1 || d.height > 1 || !nearestOutline({...d,x:.5,y:.5},area().outline); }))) {
        say('A selected patch is too large for this bag. Remove it or choose another bag.'); return;
      }
      const group = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const baseProperties = { 'Applied Patches': Array.from(selections.values()).map((s) => s.patch.title).join(', ') || 'None', '_Studio group': group };
      const monogram = query('[data-bcc-monogram]')?.value.trim();
      if (monogram) baseProperties.Monogram = monogram;
      const items = [{ id: variant.id, quantity: 1, properties: baseProperties }];
      selections.forEach((s) => {
        const properties = { '_Studio group': group, 'For bag': root.dataset.baseTitle };
        s.nodes.forEach((node, i) => {
          if (node.part.crop) properties[`_Patch ${i + 1} source`] = `Image ${node.part.sourceImageIndex}, piece ${node.part.pieceIndex}, crop ${JSON.stringify(node.part.crop)}`;
          properties[`_Patch ${i + 1} placement`] = `Image ${node.part.sourceImageIndex || s.patch.images.indexOf(node.part) + 1}: x=${(node.x * 100).toFixed(2)}%, y=${(node.y * 100).toFixed(2)}%, width=${(node.width * 100).toFixed(2)}%, height=${(node.height * 100).toFixed(2)}% of patch area`;
        });
        if (s.monogram) properties.Monogram = s.monogram;
        items.push({ id: s.patch.variantId, quantity: quantity(s), properties });
      });
      // Preview placement is deliberately not captured as an image or uploaded.
      busy = true; update(); const label = cta.textContent; cta.textContent = 'ADDING…'; say('');
      const route = window.Shopify?.routes?.root || '/';
      try {
        const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0,5);
        const response = await fetch(`${route}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: window.location.pathname }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add your selection. Please try again.');
        if (dialog.open) { dialog.close(); restoreStudio(); }
        document.dispatchEvent(new CustomEvent('cart:update', {bubbles:true, detail:{resource:result,sourceId:root.id || 'becco-studio',data:{source:'product-form-component',itemCount:items.reduce((sum,item)=>sum+item.quantity,0),sections:result.sections}}}));
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer?.open) drawer.open();
        say('Your bag and patches have been added to the cart.');
      } catch (error) { say(error.message || 'Connection error. Please try again.'); }
      finally { busy = false; cta.textContent = label; update(); }
    });
    // Move the existing section rather than clone it: all selections and listeners survive.
    const home = document.createComment('Becco Studio original position');
    root.before(home);
    const dialog = document.createElement('dialog');
    dialog.className = 'bcc-studio-dialog';
    dialog.setAttribute('aria-label', 'Customize your bag with patches');
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'bcc-studio-dialog__close';
    close.textContent = '×'; close.setAttribute('aria-label', 'Close studio');
    dialog.append(close); document.body.append(dialog);
    let opener = null;
    let previousOverflow = '';
    function restoreStudio() {
      if (!dialog.contains(root)) return;
      home.after(root);
      document.body.style.overflow = previousOverflow;
      layout(); opener?.focus({ preventScroll: true });
    }
    function openStudio(trigger = query('[data-bcc-open-studio]')) {
      if (dialog.open) return;
      opener = trigger; previousOverflow = document.body.style.overflow;
      dialog.append(root); dialog.showModal(); document.body.style.overflow = 'hidden';
      layout(); close.focus();
    }
    on(document, 'click', (event) => {
      const trigger = event.target.closest('[data-bcc-open-studio]');
      if (!trigger || trigger.dataset.bccOpenStudio !== root.dataset.baseProductId || dialog.open) return;
      event.preventDefault(); openStudio(trigger);
    });
    on(close, 'click', () => dialog.close());
    on(dialog, 'close', restoreStudio);
    on(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    on(image, 'load', layout);
    // Keep one working color picker; restore its desktop position on resize.
    const colorPicker = query('.bcc-studio__variants');
    const colorHome = document.createComment('Studio desktop colors');
    const mobileLayout = window.matchMedia('(max-width: 989px)');
    if (colorPicker) colorPicker.before(colorHome);
    function placeColors() {
      if (!colorPicker) return;
      if (mobileLayout.matches) canvas.before(colorPicker);
      else colorHome.after(colorPicker);
      layout();
    }
    on(mobileLayout, 'change', placeColors);
    placeColors();
    on(abort.signal, 'abort', () => { if (colorPicker) colorHome.after(colorPicker); colorHome.remove(); });
    const observer = new ResizeObserver(layout); observer.observe(canvas);
    instances.set(root, () => { dragCleanup?.(); if (dialog.open) dialog.close(); restoreStudio(); dialog.remove(); home.remove(); deletePiece.remove(); observer.disconnect(); artworkObserver?.disconnect(); abort.abort(); cancelAnimationFrame(frame); instances.delete(root); });
    async function loadCatalog() {
      if (loadingCatalog || !pagination.next) return;
      loadingCatalog = true;
      const status = query('[data-bcc-catalog-status]');
      const retry = query('[data-bcc-catalog-retry]');
      if (retry) retry.hidden = true;
      try {
        const visited = new Set();
        while (pagination.next && !abort.signal.aborted) {
          if (status) status.textContent = `Loading all patches… ${catalog.size} of ${pagination.total}`;
          const url = new URL(pagination.next, window.location.href);
          if (url.origin !== window.location.origin) throw new Error('Invalid catalog URL.');
          url.searchParams.set('section_id', root.dataset.sectionId);
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          if (visited.has(url.href)) throw new Error('Catalog pagination repeated a page.');
          visited.add(url.href);
          const response = await fetch(url, {signal: abort.signal});
          if (!response.ok) throw new Error('Unable to load remaining patches.');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!doc.querySelector('[data-bcc-patches]') || !doc.querySelector('[data-bcc-pagination]')) throw new Error('Invalid catalog page.');
          const batch = read(doc, '[data-bcc-patches]', []);
          const newButtons = new Map(Array.from(doc.querySelectorAll('[data-bcc-patch]')).map(b => [b.dataset.id,b]));
          let added = 0;
          const fragment = document.createDocumentFragment();
          batch.forEach(p => {
            const id = String(p.id), button = newButtons.get(id);
            if (catalog.has(id) || !button) return;
            catalog.set(id,p); patches.push(p); buttons.push(button); bindPatch(button); fragment.append(button); added++;
          });
          // A page containing only already-loaded patches is
          // valid. Follow its next cursor instead of dropping later products.
          if (added) preserveTrayPosition(() => { tray.append(fragment); sortCatalog(); filter(); });
          pagination = read(doc, '[data-bcc-pagination]', {});
          sortCatalog(); filter(); update();
        }
        if (status) status.textContent = `${catalog.size} designs loaded`;
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (status) status.textContent = `${catalog.size} designs loaded. Some patches could not load.`;
          if (retry) retry.hidden = false;
        }
      } finally { loadingCatalog = false; }
    }
    on(query('[data-bcc-catalog-retry]'), 'click', loadCatalog);
    function observeArtwork() {
      buttons.filter(b => !b.dataset.pieceId).forEach(b => { artworkObserver?.unobserve(b); artworkObserver?.observe(b); });
      pumpArtwork();
    }
    const visibilityObserver = new IntersectionObserver(entries => {
      studioVisible = entries.some(entry => entry.isIntersecting);
      if (studioVisible) { observeArtwork(); loadCatalog(); }
    }, {rootMargin:'200px'});
    visibilityObserver.observe(root);
    abort.signal.addEventListener('abort', () => visibilityObserver.disconnect(), {once:true});
    changeVariant(variant?.id); filter(); layout(); update();
  }
  function init(scope = document) { scope.querySelectorAll('[data-bcc-studio-root]').forEach(initStudio); }
  init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-bcc-studio-root]').forEach((root) => instances.get(root)?.()));
})();
